/**
 * GPS hook — wraps watchPosition with sane defaults for field use.
 * Returns the most recent fix + accuracy + a "stale" flag so callers
 * can warn when the fix is older than 60s.
 */
import { useEffect, useState } from 'react';

export interface GpsFix {
  lat: number;
  lon: number;
  accuracy_m: number;
  /** epoch ms when this fix was reported by the OS. */
  timestamp: number;
}

export interface GpsState {
  fix: GpsFix | null;
  /** OS error code or denied/unavailable reason. */
  error: string | null;
  /** True when navigator.geolocation isn't usable (no permission, etc). */
  unavailable: boolean;
}

export function useGpsFix(active = true): GpsState {
  const [state, setState] = useState<GpsState>({
    fix: null,
    error: null,
    unavailable: false,
  });

  useEffect(() => {
    if (!active) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setState({ fix: null, error: 'unsupported', unavailable: true });
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setState({
          fix: {
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            accuracy_m: pos.coords.accuracy,
            timestamp: pos.timestamp,
          },
          error: null,
          unavailable: false,
        });
      },
      (err) => {
        setState((s) => ({ ...s, error: err.message, unavailable: err.code === 1 /* PERMISSION_DENIED */ }));
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 30_000,
      },
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  }, [active]);

  return state;
}

// ── Geofence helpers ───────────────────────────────────────────────────

const EARTH_RADIUS_M = 6_371_000;

/** Haversine — accurate for the small distances field techs care about. */
export function haversineMeters(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Simple severity for color-coding the GPS fix in the UI. */
export function gpsAccuracyBucket(accuracy_m: number | null | undefined): 'good' | 'ok' | 'poor' {
  if (accuracy_m == null) return 'poor';
  if (accuracy_m < 5) return 'good';
  if (accuracy_m < 20) return 'ok';
  return 'poor';
}
